import type {
  AnalyzeRequest,
  AnalyzeResponse,
  UploadUrlRequest,
  UploadUrlResponse,
  StatusResponse,
  ApiErrorBody,
} from '@fishymeter/types';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokenGetter = () => string | null;

let tokenGetter: TokenGetter = () => null;

export function setAuthTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

async function request<T>(path: string, init?: RequestInit & { explicitToken?: string }): Promise<T> {
  const token = init?.explicitToken ?? tokenGetter();
  const { explicitToken: _explicitToken, ...fetchInit } = init ?? {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchInit,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...fetchInit.headers,
    },
  });

  const text = await res.text();

  // Not every non-2xx response comes from our own code. An edge layer -- WAF,
  // CloudFront -- answers with an HTML error page, and JSON.parse on that threw
  // a raw "Unrecognized token '<'" at the user instead of anything actionable.
  let body: unknown;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      throw new ApiError(
        res.ok ? 'BAD_RESPONSE' : 'REQUEST_REJECTED',
        `The server returned an unexpected response (HTTP ${res.status}).`,
        res.status,
        undefined,
      );
    }
  }

  if (!res.ok) {
    const errBody = body as ApiErrorBody | undefined;
    throw new ApiError(
      errBody?.error ?? 'UNKNOWN',
      errBody?.message ?? res.statusText,
      res.status,
      errBody,
    );
  }
  return body as T;
}

export interface MeProfile {
  user_id: string;
  username: string;
  email?: string;
  created_at: string;
  terms_accepted_at?: string;
}

export interface MeHistoryItem {
  analysis_id: string;
  title?: string;
  grade?: string;
  overall_score?: number;
  fish_label?: string;
  status: string;
  is_public: boolean;
  created_at: string;
}

export interface MeHistoryResponse {
  items: MeHistoryItem[];
  next_cursor?: string;
}

export interface FeedItem {
  analysis_id: string;
  title?: string;
  verdict?: string;
  grade?: string;
  overall_score?: number;
  fish_label?: string;
  username?: string;
  status: string;
  is_public: boolean;
  created_at: string;
}

export interface FeedResponse {
  items: FeedItem[];
}

export const api = {
  submitAnalysis(input: AnalyzeRequest): Promise<AnalyzeResponse> {
    return request<AnalyzeResponse>('/api/analyze', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * Step 1 of the file flow: ask for an upload slot. Returns an opaque
   * file_id plus a presigned PUT. No analysis exists yet, so abandoning here
   * costs nothing and consumes no daily quota.
   */
  requestUploadUrl(input: UploadUrlRequest): Promise<UploadUrlResponse> {
    return request<UploadUrlResponse>('/api/analyze/upload-url', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getStatus(id: string): Promise<StatusResponse> {
    return request<StatusResponse>(`/api/analyze/${id}/status`);
  },
  getMe(explicitToken?: string): Promise<MeProfile> {
    return request<MeProfile>('/api/me', { explicitToken });
  },
  setUsername(username: string, termsAcceptedAt: string): Promise<MeProfile> {
    return request<MeProfile>('/api/me/username', {
      method: 'POST',
      body: JSON.stringify({ username, terms_accepted_at: termsAcceptedAt }),
    });
  },
  getMyHistory(cursor?: string): Promise<MeHistoryResponse> {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return request<MeHistoryResponse>(`/api/me/history${qs}`);
  },
  toggleCatchPrivacy(id: string, isPublic: boolean): Promise<{ analysis_id: string; is_public: boolean }> {
    return request(`/api/catch/${id}/privacy`, {
      method: 'PATCH',
      body: JSON.stringify({ is_public: isPublic }),
    });
  },
  deleteCatch(id: string): Promise<{ analysis_id: string; deleted: true }> {
    return request(`/api/catch/${id}`, { method: 'DELETE' });
  },
  getRecentFeed(): Promise<FeedResponse> {
    return request<FeedResponse>('/api/feed/recent');
  },
  getFreshestFeed(): Promise<FeedResponse> {
    return request<FeedResponse>('/api/feed/freshest');
  },
  getFeatureFlags(): Promise<FeatureFlags> {
    return request<FeatureFlags>('/api/flags');
  },
  submitFeedback(input: { message: string; analysis_id?: string }): Promise<{
    feedback_id: string;
    created_at: string;
  }> {
    return request('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

export interface FeatureFlags {
  /** Covers image AND pdf: one upload mechanism, one flag. */
  analyzeFile: boolean;
  analyzeUrl: boolean;
  analyzeText: boolean;
  feedRecent: boolean;
  feedFreshest: boolean;
}

/**
 * Used by the FeatureFlagsProvider when /api/flags errors. Fail-closed:
 * any request that doesn't get a clean answer treats every module as off.
 * Per the Slice 15 spec: better to hide a real feature than to leak an
 * unintended one.
 */
export const FAIL_CLOSED_FLAGS: FeatureFlags = {
  analyzeFile: false,
  analyzeUrl: false,
  analyzeText: false,
  feedRecent: false,
  feedFreshest: false,
};

/**
 * PUT a file directly to S3 using a presigned URL from analyze-submit.
 * Content-Type MUST match what was declared when requesting the upload URL —
 * the presigned URL signs that header. Mismatch = S3 returns 403.
 */
export async function uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed: HTTP ${res.status}`);
  }
}
